import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";

export type Partner = {
  id: string;
  code: string;
  name: string;
  shortCode: string;
  isActive: boolean;
  notes: string | null;
  userId: string | null;
  user: { id: string; name: string } | null;
};

export type PartnerPayload = {
  name?: string;
  shortCode?: string;
  userId?: string | null;
  notes?: string | null;
  isActive?: boolean;
};

export type OwnershipSnapshot = {
  effectiveFrom: string;
  holders: {
    partnerId: string;
    partnerName: string;
    shortCode: string;
    shares: number;
    percent: number;
  }[];
};

export type CompanyOwnership = {
  id: string;
  code: string;
  name: string;
  currency: string;
  status: string;
  current: OwnershipSnapshot | null;
  history: OwnershipSnapshot[];
};

export type OwnershipPayload = {
  companyId: string;
  effectiveFrom: string;
  holders: { partnerId: string; shares: number }[];
};

const PARTNERS_KEY = ["partners"] as const;

export function usePartnerList(): UseQueryResult<Partner[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PARTNERS_KEY, "list", accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ partners: Partner[] }>("/partners", { token: accessToken });
      return res.partners;
    },
  });
}

export function useCompanyOwnership(): UseQueryResult<CompanyOwnership[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...PARTNERS_KEY, "ownership", accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ companies: CompanyOwnership[] }>("/partners/ownership", {
        token: accessToken,
      });
      return res.companies;
    },
  });
}

export function useSavePartner() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: PartnerPayload }) =>
      apiFetch(id ? `/partners/${id}` : "/partners", {
        method: id ? "PUT" : "POST",
        token: accessToken,
        body: payload,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PARTNERS_KEY }),
  });
}

export function useSaveOwnership() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, ...payload }: OwnershipPayload) =>
      apiFetch(`/partners/ownership/${companyId}`, {
        method: "PUT",
        token: accessToken,
        body: payload,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PARTNERS_KEY }),
  });
}

export function useRemoveOwnership() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, effectiveFrom }: { companyId: string; effectiveFrom: string }) =>
      apiFetch(`/partners/ownership/${companyId}/${effectiveFrom}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PARTNERS_KEY }),
  });
}
